import { Medal, MapPin, Quote } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import { BUSINESS } from "../data/site";

const CHAMBERS = [
  { name: "Chino Valley Chamber of Commerce", url: "https://www.chinovalley.org/" },
  { name: "Prescott Valley Chamber of Commerce", url: "https://www.pvchamber.org/" },
  { name: "Prescott Chamber of Commerce", url: "https://www.prescott.org/" },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Us"
        description="Yavapai Waste Solutions is a locally owned, veteran-owned waste company in Prescott, AZ. Meet co-owners Nathaniel Murphy and Kay Lopez."
        path="/about"
      />
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} />
          <div className="mt-8 max-w-3xl">
            <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900 leading-[1.05]">A Local Waste Company That Answers the Phone</h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Yavapai Waste Solutions is locally owned and operated in Prescott, Arizona — built around reliable service, straightforward pricing, and real people you can reach.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-50 border border-sky-100 text-sm font-heading font-semibold text-sky-800">
                <MapPin className="h-4 w-4" /> Locally Owned & Operated
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-50 border border-sky-100 text-sm font-heading font-semibold text-sky-800">
                <Medal className="h-4 w-4" /> Veteran Owned
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-ink text-white" data-testid="philosophy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Quote className="h-10 w-10 text-sky-400 mx-auto mb-6" aria-hidden="true" />
          <blockquote className="font-heading font-bold text-3xl sm:text-4xl leading-tight tracking-tight">
            "We live and die by our customer service."
          </blockquote>
          <p className="mt-5 text-slate-400">— Nathaniel Murphy, Co-Owner</p>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="owners">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl text-slate-900 tracking-tight text-center">Meet the Owners</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-8">
            <Reveal>
              <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white hover:shadow-xl transition-shadow" data-testid="owner-nathaniel">
                <img src="/assets/yws/uploads/nathaniel-portrait.webp" alt="Nathaniel Murphy, Owner & Logistics at Yavapai Waste Solutions" className="w-full aspect-[4/5] object-cover object-top" loading="lazy" />
                <div className="p-6">
                  <h3 className="font-heading font-bold text-2xl text-slate-900">Nathaniel Murphy</h3>
                  <p className="text-sm font-heading font-semibold text-sky-700 mt-1">Co-Owner · Owner & Logistics · U.S. Air Force Veteran</p>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    Nathaniel runs logistics with the precision you'd expect from an Air Force veteran — the right dumpster, in the right place, when it's supposed to be there.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white hover:shadow-xl transition-shadow" data-testid="owner-kay">
                <img src="/assets/yws/uploads/kay-portrait.webp" alt="Kay Lopez, Owner & Sales at Yavapai Waste Solutions" className="w-full aspect-[4/5] object-cover object-top" loading="lazy" />
                <div className="p-6">
                  <h3 className="font-heading font-bold text-2xl text-slate-900">Kay Lopez</h3>
                  <p className="text-sm font-heading font-semibold text-sky-700 mt-1">Co-Owner · Owner & Sales · 30+ Years of Local Expertise</p>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    Kay knows the Prescott area inside and out — the neighborhoods, the businesses, the job sites. When you call YWS, she's the expertise on the other end of the line.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-y border-slate-200" data-testid="community">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl text-slate-900 tracking-tight text-center">Rooted in the Community</h2>
            <p className="text-center text-slate-600 mt-3 max-w-2xl mx-auto">
              From our ribbon-cutting with the Prescott Valley Chamber of Commerce to every job site we serve — this community is home.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            <Reveal>
              <figure className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                <img src="/assets/yws/uploads/ribbon-cutting-truck.webp" alt="The YWS team at their ribbon-cutting in front of a branded Yavapai Waste Solutions truck" className="w-full aspect-[4/3] object-cover" loading="lazy" data-testid="community-ribbon" />
                <figcaption className="px-5 py-3.5 text-sm font-medium text-slate-700">The YWS ribbon-cutting with the Prescott Valley Chamber of Commerce</figcaption>
              </figure>
            </Reveal>
            <Reveal delay={0.1}>
              <figure className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                <img src="/assets/yws/uploads/team-chamber-award.jpeg" alt="Nathaniel Murphy and the YWS team receiving a certificate of appreciation from the Prescott Valley Chamber" className="w-full aspect-[4/3] object-cover" loading="lazy" data-testid="community-award" />
                <figcaption className="px-5 py-3.5 text-sm font-medium text-slate-700">Recognized by the Prescott Valley Chamber of Commerce</figcaption>
              </figure>
            </Reveal>
          </div>

          <Reveal className="mt-10">
            <div className="bg-white border border-slate-200 rounded-2xl p-7 text-center">
              <h2 className="font-heading font-bold text-xl text-slate-900">Proud Local Members</h2>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                {CHAMBERS.map((c) => (
                  <a key={c.name} href={c.url} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-sm font-medium text-slate-700 hover:border-sky-400 hover:text-sky-700 transition-colors" data-testid={`chamber-${c.name.split(" ")[0].toLowerCase()}`}>
                    {c.name}
                  </a>
                ))}
              </div>
              <p className="mt-6 text-sm text-slate-500 italic">And yes — around here, we talk trash.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection title="Work With a Local Team" subtitle={`Call ${BUSINESS.phone} or request a dumpster online. Monday–Friday, 8 AM–5 PM.`} />
    </>
  );
}
